// Report screens (T19): the summary, then the full report with filters. Both are free, with
// no sign-up (the email gate, T20, was removed before launch).
// Print styles in app.css make this the MVP export (browser print to PDF). The rota chart
// (T29) is a separate PDF of the planned shifts, made in the worker.
import { useState } from 'preact/hooks';
import type { PersonReport, Report, ReportFinding } from '../../report/buildReport';
import { SEVERITY_LABELS } from '../../report/labels';
import type { RuleId, Severity } from '../../rules/types';
import { Disclaimer, PrivacyNote } from './Disclaimer';
import { Timeline } from './Timeline';

const SEVERITIES: Severity[] = ['breach', 'risk', 'info'];

interface Props {
  report: Report;
  onEditColumns(): void;
  onAddDetails(): void;
  onStartAgain(): void;
  /** Makes the rota chart PDF and saves it; rejects if it couldn't. */
  onDownloadChart(): Promise<void>;
}

export function ReportView({
  report,
  onEditColumns,
  onAddDetails,
  onStartAgain,
  onDownloadChart,
}: Props) {
  const [chart, setChart] = useState<'ready' | 'making' | 'failed'>('ready');
  async function downloadChart() {
    setChart('making');
    try {
      await onDownloadChart();
      setChart('ready');
    } catch {
      setChart('failed');
    }
  }

  return (
    <>
      <SummarySection report={report} onAddDetails={onAddDetails} />
      <FullReport report={report} />
      <div class="actions no-print">
        <button type="button" disabled={chart === 'making'} onClick={() => void downloadChart()}>
          {chart === 'making' ? 'Making the rota chart…' : 'Download rota chart (PDF)'}
        </button>
        <button type="button" onClick={onEditColumns}>
          Change how we read your file
        </button>
        <button type="button" onClick={onAddDetails}>
          Add details
        </button>
        <button type="button" onClick={onStartAgain}>
          Check another rota
        </button>
      </div>
      {chart === 'failed' && (
        <p role="alert" class="no-print">
          We couldn't make the rota chart. Please try again.
        </p>
      )}
    </>
  );
}

function SummarySection({ report, onAddDetails }: { report: Report; onAddDetails(): void }) {
  const s = report.summary;
  return (
    <section aria-labelledby="summary-heading">
      <h2 id="summary-heading">Your rota check</h2>
      <PrivacyNote />
      <p>
        Based on the {plural(s.shiftCount, 'planned shift')} for{' '}
        {plural(s.peopleCount, 'person', 'people')}
        {s.siteCount > 1 ? ` across ${s.siteCount} sites` : ''},{' '}
        {dateRange(s.period.start, s.period.end)}.
      </p>
      <ul class="tiles" aria-label="Findings by type">
        {SEVERITIES.map((severity) => (
          <li key={severity} class={`tile severity-${severity}`}>
            <span class="tile-count">{s.counts[severity]}</span>{' '}
            <span>{SEVERITY_LABELS[severity]}</span>
          </li>
        ))}
      </ul>
      {s.peopleAffected > 0 ? (
        <p>
          {s.peopleAffected === 1 ? '1 person has' : `${s.peopleAffected} people have`} at least one
          thing to act on or check.
        </p>
      ) : (
        <p>We didn't find anything that needs action or checking.</p>
      )}
      {s.topIssues.length > 0 && (
        <>
          <h3>Main things we found</h3>
          <ul>
            {s.topIssues.map((issue) => (
              <li key={issue.text + issue.severity}>
                <span class={`badge severity-${issue.severity}`}>
                  {SEVERITY_LABELS[issue.severity]}
                </span>{' '}
                {issue.text}: {plural(issue.people, 'person', 'people')}
                {issue.times > issue.people ? `, ${issue.times} times` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      {report.assumptions.length > 0 && (
        <aside class="assumptions" aria-labelledby="assumptions-heading">
          <h3 id="assumptions-heading">Assumptions we've made</h3>
          <p>To keep things quick, we've assumed:</p>
          <ul>
            {report.assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
          <button type="button" class="no-print" onClick={onAddDetails}>
            Add details
          </button>
        </aside>
      )}
      {s.byRule.length > 0 && (
        <div class="table-scroll">
          <table>
            <caption>Findings by check</caption>
            <thead>
              <tr>
                <th scope="col">Check</th>
                {SEVERITIES.map((sev) => (
                  <th key={sev} scope="col">
                    {SEVERITY_LABELS[sev]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.byRule.map((row) => (
                <tr key={row.ruleId}>
                  <th scope="row">{row.title}</th>
                  <td>{row.breach}</td>
                  <td>{row.risk}</td>
                  <td>{row.info}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Disclaimer />
    </section>
  );
}

function FullReport({ report }: { report: Report }) {
  const [person, setPerson] = useState('');
  const [rule, setRule] = useState<RuleId | ''>('');
  const [shown, setShown] = useState<Record<Severity, boolean>>({
    breach: true,
    risk: true,
    info: true,
  });

  const keep = (f: ReportFinding) => shown[f.severity] && (rule === '' || f.ruleId === rule);
  const people = report.people
    .filter((p) => person === '' || p.ref === person)
    .map((p) => ({ ...p, findings: p.findings.filter(keep) }))
    .filter((p) => p.findings.length > 0);
  const general = person === '' ? report.general.filter(keep) : [];
  const rules = [...new Map(report.summary.byRule.map((r) => [r.ruleId, r.title])).entries()];

  return (
    <section aria-labelledby="full-heading">
      <h2 id="full-heading">Full report</h2>
      <fieldset class="filters no-print">
        <legend>Show</legend>
        <label>
          Person
          <select value={person} onChange={(e) => setPerson(e.currentTarget.value)}>
            <option value="">Everyone</option>
            {report.people.map((p) => (
              <option key={p.ref} value={p.ref}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Check
          <select value={rule} onChange={(e) => setRule(e.currentTarget.value as RuleId | '')}>
            <option value="">All checks</option>
            {rules.map(([id, title]) => (
              <option key={id} value={id}>
                {title}
              </option>
            ))}
          </select>
        </label>
        <div class="checkboxes">
          {SEVERITIES.map((sev) => (
            <label key={sev}>
              <input
                type="checkbox"
                checked={shown[sev]}
                onChange={(e) => setShown({ ...shown, [sev]: e.currentTarget.checked })}
              />{' '}
              {SEVERITY_LABELS[sev]}
            </label>
          ))}
        </div>
        <button type="button" onClick={() => window.print()}>
          Print or save as PDF
        </button>
      </fieldset>

      {general.length > 0 && (
        <section aria-labelledby="general-heading">
          <h3 id="general-heading">About your whole rota</h3>
          <FindingList findings={general} />
        </section>
      )}

      {people.length === 0 && general.length === 0 && <p>Nothing matches these filters.</p>}
      {people.map((p) => (
        <PersonSection key={p.ref} person={p} />
      ))}

      {report.importNotes.length > 0 && (
        <section aria-labelledby="import-heading">
          <h3 id="import-heading">Things we noticed in your file</h3>
          <ul>
            {report.importNotes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="cant-heading">
        <h3 id="cant-heading">What we can't check</h3>
        <ul>
          {report.cantCheck.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </section>
      <Disclaimer />
    </section>
  );
}

function PersonSection({ person }: { person: PersonReport }) {
  const id = `person-${person.ref.replace(/[^a-z0-9]/gi, '-')}`;
  // Kept by identity, not position, so changing the filters can't move the highlight
  // onto a different finding.
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const pickedIndex = person.findings.findIndex((f) => findingKey(f) === pickedKey);
  const picked = pickedIndex < 0 ? null : pickedIndex;
  const highlighted = new Set(picked === null ? [] : (person.findings[picked]?.shiftIds ?? []));

  const pick = (index: number) => {
    const finding = person.findings[index];
    if (finding) setPickedKey(findingKey(finding));
  };
  const pickShift = (shiftId: string) => {
    const index = person.findings.findIndex((f) => f.shiftIds.includes(shiftId));
    if (index < 0) return;
    pick(index);
    document.getElementById(`${id}-finding-${index}`)?.focus();
  };
  const showOnTimeline = (index: number) => {
    pick(index);
    document.getElementById(`${id}-timeline`)?.scrollIntoView({ block: 'nearest' });
  };

  return (
    <section aria-labelledby={id} class="person">
      <h3 id={id}>{person.name}</h3>
      {person.timelineTooBig && (
        <p class="muted">
          {person.name} has too many shifts in this rota to draw a week view, so their findings are
          listed without one.
        </p>
      )}
      {person.timeline.length > 0 && (
        <div id={`${id}-timeline`}>
          <Timeline
            weeks={person.timeline}
            night={person.night}
            highlighted={highlighted}
            onPickShift={pickShift}
          />
        </div>
      )}
      <FindingList
        findings={person.findings}
        idPrefix={id}
        picked={picked}
        onShowOnTimeline={person.timeline.length > 0 ? showOnTimeline : undefined}
      />
    </section>
  );
}

function findingKey(f: ReportFinding): string {
  return `${f.ruleId}|${f.severity}|${f.dateLabel ?? ''}|${f.shiftIds.join(',')}`;
}

interface FindingListProps {
  findings: ReportFinding[];
  idPrefix?: string;
  picked?: number | null;
  onShowOnTimeline?: ((index: number) => void) | undefined;
}

function FindingList({ findings, idPrefix, picked, onShowOnTimeline }: FindingListProps) {
  return (
    <ul class="findings">
      {findings.map((f, i) => (
        <li
          key={i}
          id={idPrefix ? `${idPrefix}-finding-${i}` : undefined}
          tabIndex={idPrefix ? -1 : undefined}
          class={`finding severity-${f.severity}${picked === i ? ' picked' : ''}`}
        >
          <p class="finding-head">
            <span class={`badge severity-${f.severity}`}>{SEVERITY_LABELS[f.severity]}</span>{' '}
            <strong>{f.ruleTitle}</strong>
            {f.dateLabel && <span class="finding-date">{f.dateLabel}</span>}
          </p>
          <p>{f.explanation}</p>
          {f.fix && (
            <p class="fix">
              <strong>How you could fix this:</strong> {f.fix}
            </p>
          )}
          {f.shifts.length > 0 && (
            <>
              <ul class="shift-chips" aria-label={f.shifts.length === 1 ? 'Shift' : 'Shifts'}>
                {f.shifts.map((s) => (
                  <li key={s.sourceRow}>
                    row {s.sourceRow} ({s.label})
                  </li>
                ))}
                {f.moreShifts > 0 && (
                  <li class="more">
                    and {f.moreShifts} more {f.moreShifts === 1 ? 'shift' : 'shifts'}
                  </li>
                )}
              </ul>
              {onShowOnTimeline && (
                <p class="no-print">
                  <button
                    type="button"
                    class="link-button"
                    aria-pressed={picked === i}
                    onClick={() => onShowOnTimeline(i)}
                  >
                    Show on the week view
                  </button>
                </p>
              )}
            </>
          )}
          {f.sourceUrl && (
            <p class="source">
              Source:{' '}
              <a href={f.sourceUrl} target="_blank" rel="noopener noreferrer">
                {sourceName(f.sourceUrl)}
              </a>
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function sourceName(url: string): string {
  const reg = /legislation\.gov\.uk\/uksi\/1998\/1833\/regulation\/([0-9A-Z]+)/.exec(url);
  if (reg) return `Working Time Regulations 1998, regulation ${reg[1]}`;
  return url.replace(/^https:\/\/www\./, '');
}

function dateRange(start: string, end: string): string {
  const fmt = (d: string) =>
    new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${d}T12:00:00Z`));
  return start === end ? fmt(start) : `${fmt(start)} to ${fmt(end)}`;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
